import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Target,
  BookOpen,
  Clock,
  Award,
  CheckCircle2,
  Lock,
  Play,
  ChevronRight,
  Users,
  Star,
  Loader2,
} from 'lucide-react';
import { useLearningPath, useMyEnrollments, useEnrollInPath } from '@/api/hooks';
import { ProgressRing, CourseCard } from '@/components/learning';
import type { Course, PathEnrollment } from '@/api/client';
import { useTrackEntityView } from '@/hooks/use-entity-tracker';
import { resolveMediaUrl } from '@/lib/media';
import { MediaImage } from '@/components/media';

export const Route = createFileRoute('/learn/paths/$pathId')({
  component: PathDetailPage,
});

function PathDetailPage() {
  const { pathId } = Route.useParams();
  const navigate = useNavigate();

  const { data: path, isLoading: pathLoading, error: pathError } = useLearningPath(pathId);
  const { data: enrollments = [] } = useMyEnrollments();
  const enrollMutation = useEnrollInPath();

  // Find user's enrollment in this path
  const enrollment = enrollments.find((e) => e.path_id === pathId) as PathEnrollment | undefined;
  const isEnrolled = !!enrollment;
  const progress = enrollment?.progress_percentage ?? 0;
  const completedCourses = enrollment?.completed_courses ?? 0;

  // Track learning path view
  useTrackEntityView({
    entityType: 'learning_path',
    entityId: pathId,
    entityName: path?.name || 'Untitled Path',
    metadata: {
      totalCourses: path?.course_count || path?.courses?.length || 0,
      progress,
      isEnrolled,
      completedCourses,
      category: path?.category,
      difficulty: path?.difficulty,
    },
  });

  const handleEnroll = () => {
    enrollMutation.mutate(pathId, {
      onSuccess: () => {
        // Enrollment successful - UI will update via query invalidation
      },
    });
  };

  const handleContinue = () => {
    // Find the first incomplete course and navigate to it
    if (path?.courses && path.courses.length > 0) {
      const nextCourse = path.courses[completedCourses] || path.courses[0];
      navigate({ to: '/learn/courses/$courseId', params: { courseId: nextCourse.id } });
    }
  };

  const handleCourseClick = (course: Course, index: number) => {
    // Don't allow clicking locked courses
    if (!isEnrolled || index > completedCourses) return;

    navigate({ to: '/learn/courses/$courseId', params: { courseId: course.id } });
  };

  if (pathLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (pathError || !path) {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Learning
        </Link>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">Path Not Found</h2>
          <p className="text-muted-foreground">
            The learning path you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const courses = path.courses || [];
  const courseCount = path.course_count ?? courses.length;
  const totalCourses = enrollment?.total_courses ?? courseCount;

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Back navigation */}
      <Link
        to="/learn"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Learning
      </Link>

      {/* Path Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-xl border border-border overflow-hidden mb-6"
      >
        {/* Banner */}
        {path.banner_url ? (
          <div className="h-48 relative">
            <MediaImage
              src={resolveMediaUrl(path.banner_url)}
              alt={path.name}
              className="w-full h-full"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
          </div>
        ) : (
          <div
            className="h-32"
            style={{
              background: path.color
                ? `linear-gradient(135deg, ${path.color}, ${path.color}99)`
                : 'linear-gradient(135deg, rgb(147, 51, 234), rgb(79, 70, 229))',
            }}
          />
        )}

        <div className="p-6 -mt-8 relative">
          <div className="flex items-start gap-4">
            {/* Icon/Thumbnail */}
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0"
              style={{
                background: path.color
                  ? `linear-gradient(135deg, ${path.color}, ${path.color}99)`
                  : 'linear-gradient(135deg, rgb(147, 51, 234), rgb(79, 70, 229))',
              }}
            >
              {path.thumbnail_url ? (
                <MediaImage
                  src={resolveMediaUrl(path.thumbnail_url)}
                  alt={path.name}
                  className="w-full h-full rounded-xl"
                />
              ) : (
                <Target className="w-8 h-8 text-white" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {path.is_featured && (
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                )}
                {path.difficulty && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      path.difficulty === 'beginner'
                        ? 'bg-green-500/20 text-green-400'
                        : path.difficulty === 'intermediate'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-orange-500/20 text-orange-400'
                    }`}
                  >
                    {path.difficulty}
                  </span>
                )}
                {path.category && (
                  <span className="text-xs text-muted-foreground">{path.category}</span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">{path.name}</h1>
              <p className="text-muted-foreground">
                {path.description || path.short_description || 'No description available'}
              </p>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-6 mt-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4" />
              <span>{courseCount} course{courseCount !== 1 ? 's' : ''}</span>
            </div>
            {path.estimated_hours && (
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{path.estimated_hours} hours</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Award className="w-4 h-4" />
              <span>Certificate included</span>
            </div>
          </div>

          {/* Progress section (only for enrolled users) */}
          {isEnrolled && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">Your Progress</span>
                <span className="text-sm font-semibold text-foreground">
                  {completedCourses} of {totalCourses} courses completed
                </span>
              </div>
              <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-600"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {progress === 100
                  ? 'Congratulations! You have completed this learning path.'
                  : `${Math.round(progress)}% complete`}
              </p>
            </div>
          )}

          {/* Action button */}
          <div className="mt-6">
            {!isEnrolled ? (
              <button
                onClick={handleEnroll}
                disabled={enrollMutation.isPending}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {enrollMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enrolling...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Enroll Now - Start Learning
                  </>
                )}
              </button>
            ) : progress === 100 ? (
              <button className="w-full sm:w-auto px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2">
                <Award className="w-4 h-4" />
                View Certificate
              </button>
            ) : (
              <button
                onClick={handleContinue}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" />
                Continue Learning
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Learning Objectives */}
      {path.learning_objectives && path.learning_objectives.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-xl border border-border p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-foreground mb-4">What You'll Learn</h2>
          <ul className="space-y-3">
            {path.learning_objectives.map((objective, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{objective}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* Courses List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card rounded-xl border border-border p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Courses in this Path</h2>
          <span className="text-sm text-muted-foreground">
            {courseCount} course{courseCount !== 1 ? 's' : ''}
          </span>
        </div>

        {courses.length > 0 ? (
          <div className="space-y-4">
            {courses.map((course, idx) => {
              const isCompleted = isEnrolled && idx < completedCourses;
              const isCurrent = isEnrolled && idx === completedCourses;
              const isLocked = isEnrolled && idx > completedCourses;

              return (
                <div key={course.id} className="relative">
                  {/* Course number badge */}
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 z-10">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                          ? 'bg-indigo-500 text-white'
                          : isLocked
                          ? 'bg-secondary text-muted-foreground'
                          : 'bg-secondary text-foreground'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        idx + 1
                      )}
                    </div>
                  </div>

                  {/* Course card */}
                  <div className="ml-6">
                    <CourseCard
                      course={course}
                      index={idx}
                      isLocked={!isEnrolled ? false : isLocked}
                      isCompleted={isCompleted}
                      isCurrent={isCurrent}
                      progress={isCurrent ? 50 : isCompleted ? 100 : 0} // TODO: Get actual course progress
                      onClick={() => handleCourseClick(course, idx)}
                    />
                  </div>

                  {/* Connector line */}
                  {idx < courses.length - 1 && (
                    <div className="absolute left-1 top-[60%] w-0.5 h-8 bg-border" />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No courses have been added to this path yet.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
